import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Admission} from "../model";
import {NzAlertModule} from "ng-zorro-antd/alert";
import {NzDescriptionsModule} from "ng-zorro-antd/descriptions";
import {NzButtonModule} from "ng-zorro-antd/button";
import {AdmissionService} from "../admission.service";
import {Router} from "@angular/router";
import {isAfter, isBefore} from 'date-fns';
import {NzTableModule} from 'ng-zorro-antd/table';
import {SanitizeHtmlDirective} from '../../main/sanitize-html.directive';
import {NzDividerModule} from 'ng-zorro-antd/divider';
import {keyBy} from 'lodash-es';
import {FormsModule} from '@angular/forms';
import {NzCheckboxComponent} from 'ng-zorro-antd/checkbox';
import {NzCollapseModule} from 'ng-zorro-antd/collapse';
import {firstValueFrom} from 'rxjs';
import {HKDatePipe} from '../hkdate.pipe';

@Component({
  selector: 'app-admission-card',
  standalone: true,
  imports: [CommonModule, NzAlertModule, NzDescriptionsModule, NzButtonModule,
    NzTableModule, SanitizeHtmlDirective, NzDividerModule, FormsModule, NzCheckboxComponent, NzCollapseModule, HKDatePipe],
  template: `
    @if (admission.ineligible) {
      <div [sanitizeHtml]="admission.ineligible" [data]="admission"></div>
    } @else {
      <nz-collapse nzAccordion>
        <nz-collapse-panel nzHeader="Application Form" [nzActive]="!admission.within_result_period">
          <nz-descriptions nzBordered nzSize="small" [nzColumn]="2">
            <nz-descriptions-item nzTitle="Application Period" [nzSpan]="2">
              {{ admission.parameters?.application.start | hkdate : 'd MMM yyyy HH:mm' }}
              ~ {{ admission.parameters?.application.end | hkdate : 'd MMM yyyy HH:mm' }}
            </nz-descriptions-item>
            <nz-descriptions-item [nzSpan]="2" nzTitle="Residential Period">
              {{ admission.parameters?.housing.start | hkdate : 'd MMM yyyy' }}
              ~ {{ admission.parameters?.housing.end | hkdate : 'd MMM yyyy' }}
            </nz-descriptions-item>
            <nz-descriptions-item *ngIf="!!admission.commencement_date" nzTitle="Commencement Date"
                                  [nzSpan]="!admission.extra.normal_study_edate ? 2 : 1">
              {{ admission.commencement_date | hkdate : 'd MMM yyyy' }}
            </nz-descriptions-item>
            <nz-descriptions-item *ngIf="admission.extra.normal_study_edate" nzTitle="Normal Study End Date">
              {{ admission.extra.normal_study_edate | hkdate: 'd MMM yyyy' }}
            </nz-descriptions-item>
            <ng-container *ngIf="!!admission.application?.submitted_at">
              <nz-descriptions-item nzTitle="Submitted At">
                {{ admission.application?.submitted_at  | hkdate : 'd MMM yyyy HH:mm' }}
              </nz-descriptions-item>
              <nz-descriptions-item nzTitle="Application Reference No.">
                {{ admission.application?.submitted_ref }}
              </nz-descriptions-item>
            </ng-container>
          </nz-descriptions>

          <div class="notes" *ngIf="!!appMessage && appMessage.length > 0">
            <nz-alert [nzMessage]="closedMsg"></nz-alert>
            <ng-template #closedMsg>
              <div [sanitizeHtml]="appMessage"></div>
            </ng-template>
          </div>

          <div class="action-panel">
            <button *ngIf="actions.apply" (click)="selectCurrentAdm('form')" nz-button>Apply</button>
            <button *ngIf="actions.edit" (click)="selectCurrentAdm('form')" nz-button>Edit Application</button>
            <button *ngIf="actions.view" (click)="selectCurrentAdm('summary')" nz-button> View Application</button>
          </div>
        </nz-collapse-panel>
        <nz-collapse-panel nzHeader="Application Result" [nzActive]="admission.within_result_period"
                           *ngIf="admission.within_result_period">
          <nz-descriptions nzBordered nzSize="small" [nzColumn]="2">
            <nz-descriptions-item nzTitle="Application Status" [nzSpan]="2">
              {{ admission.variables.appl_status[admission.application?.appl_decision] }}
            </nz-descriptions-item>

            @if (roomTypes[admission.application?.room_type]?.room_type) {
              <nz-descriptions-item nzTitle="Residence Period" [nzSpan]="2">
                <ng-container *ngIf="admission.application?.residence_start; else appHousing">
                  {{ admission.application?.residence_start | hkdate : 'd MMM yyyy' }}
                  ~ {{ admission.application?.residence_end | hkdate : 'd MMM yyyy' }}
                </ng-container>
                <ng-template #appHousing>
                  {{ admission.parameters?.housing.start | hkdate : 'd MMM yyyy' }}
                  ~ {{ admission.parameters?.housing.end | hkdate : 'd MMM yyyy' }}
                </ng-template>
              </nz-descriptions-item>
              <nz-descriptions-item nzTitle="Room Type Allocated" [nzSpan]="2">
                {{ roomTypes[admission.application?.room_type]?.room_type }}
              </nz-descriptions-item>
              <nz-descriptions-item nzTitle="Confirmation Fees">
                HK{{ (roomTypes[admission.application?.room_type]?.conf_fee ?? admission.parameters.confirmation_fee) | currency }}
              </nz-descriptions-item>

              @if (admission.application?.paid_at) {
                <nz-descriptions-item nzTitle="Payment Settled At">
                  {{ admission.application?.paid_at | hkdate : 'd MMM yyyy, HH:mm' }}
                </nz-descriptions-item>
                <nz-descriptions-item nzTitle="Pair Up Period" [nzSpan]="2">
                  Until {{ admission.parameters?.pairup_end | hkdate : 'd MMM yyyy, HH:mm' }}
                </nz-descriptions-item>
              } @else {
                <nz-descriptions-item nzTitle="Payment Due Date">
                  {{ admission.application?.payment_due | hkdate : 'medium' }}
                </nz-descriptions-item>
              }
            }
            @for (m of admission.application?.messages; track m) {
              <nz-descriptions-item [nzTitle]="m.title" [nzSpan]="2">
                <span [sanitizeHtml]="m.message"></span>
              </nz-descriptions-item>
            }
          </nz-descriptions>
          <div class="notes" *ngIf="!!resultMessage && resultMessage.length > 0">
            <div [sanitizeHtml]="resultMessage"></div>
          </div>
          <div class="action-panel">
            <div *ngIf="actions.pay">
              <p style="margin-bottom: 20px;">
                <label nz-checkbox [(ngModel)]="notesChecked">
                  {{ prepaymentCheckbox }}
                </label>
              </p>
              <form ngNoForm #paymentForm action="srifpay_cityu.p_process_payment" method="post">
                <input #paymentInfo type="hidden" id="p_payment_info" name="p_payment_info">
                <button [disabled]="!notesChecked" type="button" (click)="processPayment()" nz-button>Process
                  Payment
                </button>
              </form>
            </div>
            <div *ngIf="actions.pair_up">
              <form ngNoForm action="hwhopairup_cityu.P_Main" method="post">
                <input type="hidden" name="p_sys_type" [value]="admission.origin">
                <input type="hidden" name="p_appl_type" value="RPG">
                <input type="hidden" name="p_mode" [value]="1">
                <input type="hidden" name="p_term_code" [value]="admission.term">
                <input type="hidden" name="p_external_info" [value]="admission.application.pair_up_info">
                <button type="submit" nz-button>Pair Up</button>
              </form>
            </div>
          </div>
        </nz-collapse-panel>
      </nz-collapse>
      @if (faqs.length > 0) {
        <div class="faqs">
          <nz-divider nzText="Frequently Ask Questions"></nz-divider>
          <nz-collapse nzAccordion>
            @for (f of faqs; track f.question; let index = $index) {
              <nz-collapse-panel nzHeader='{{index+1}}) {{f.question}}'>
                {{ f.answer }}
              </nz-collapse-panel>
            }
          </nz-collapse>
        </div>
      }
    }
  `,
  styles: [`
    .faqs {
      margin-top: 15px;
      font-size: 90%;
    }

    .action-panel {
      margin: 10px 0;
      display: flex;
    }

    .action-panel button {
      margin: 0 2px;
    }

    .notes {
      margin: 10px;
    }

    ::ng-deep .ant-descriptions-item-label {
      width: 200px;
    }
  `]
})
export class AdmissionCardComponent implements OnInit {

  @Input() admission!: Admission;

  roomTypes: any = {};

  notesChecked = false;

  actions = {
    apply: false,
    edit: false,
    view: false,
    pay: false,
    pair_up: false,
  }
  appMessage = '';
  resultMessage = '';
  prepaymentCheckbox = '';
  faqs: any[] = [];
  @ViewChild('paymentForm') paymentFormElm: any;
  @ViewChild('paymentInfo') paymentInfoElm: any;


  constructor(private appl: AdmissionService, private router: Router) {
  }

  ngOnInit(): void {
    this.roomTypes = keyBy(this.admission.variables.loding_quota_fee, 'code');
    const {application, variables, parameters} = this.admission;
    const {messages, faqs} = variables;
    const decision = application?.appl_decision;
    const appSubmitted = !!application?.submitted_ref;
    const appBegin = isAfter(new Date(), new Date(parameters.application.start));
    const appClosed = isAfter(new Date(), new Date(parameters.application.end));
    const inPairUpPeriod = isBefore(new Date(), new Date(parameters?.pairup_end));
    const appOpened = appBegin && !appClosed && decision === 'N';


    this.actions.apply = appOpened && !appSubmitted;
    this.actions.edit = appOpened && appSubmitted;
    this.actions.view = !appOpened && appSubmitted;

    if (!appOpened && !appSubmitted) {
      this.appMessage = appBegin ? messages.app_closed : messages.app_not_opened;
    } else if (this.actions.apply) {
      this.appMessage = messages.app_opened;
    }

    switch (decision) {
      case 'S':
        this.actions.pay = true;
        this.resultMessage = messages.successful;
        this.prepaymentCheckbox = messages.pre_payment_checkbox;
        break;
      case 'W':
        this.resultMessage = messages.waiting;
        break;
      case 'U':
        this.resultMessage = messages.unsuccessful;
        break;
      case 'C':
        this.actions.pair_up = inPairUpPeriod && (!['H12-S', 'JC-S', 'H7-S'].includes(application?.room_type));
        this.resultMessage = messages.post_payment + (inPairUpPeriod ? '<div>' + messages.pair_up + '</div>' : '');
        break;
    }

    this.faqs = (faqs ?? []).filter((f: any) => (f.decisions as any[]).includes(decision));
    this.appMessage = this.appMessage ?? '';
    this.resultMessage = this.resultMessage ?? '';
  }

  async processPayment() {
    const tokenInfo = await firstValueFrom(this.appl.getPaymentToken(this.admission.application.id));
    this.paymentInfoElm.nativeElement.value = tokenInfo.token;
    this.paymentFormElm.nativeElement.submit();
  }

  selectCurrentAdm(url: string) {
    this.appl.updateCurrentAdm(this.admission);
    this.router.navigate(['/', url]).then();
  }
}
